import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtDate, fileUrl } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, BookOpen, Download, Upload, Copy, Trash2, ChevronRight, Layers, Loader2, Search, X, RotateCcw } from "lucide-react";

const fmtDateTime = (s) => {
  const d = new Date(s);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const K2LastInfo = ({ last }) => {
  if (!last) return null;
  const changed = (last.created || 0) + (last.updated || 0);
  return (
    <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="k2-last-import-info">
      {last.finished_at
        ? `Poslední import: ${fmtDateTime(last.finished_at)} · ${changed} změněných receptur`
        : "Import z K2 zatím neproběhl"}
    </span>
  );
};

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", author: "", description: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState({ name: "", author: "", file: null });
  const [uploading, setUploading] = useState(false);
  const [k2Busy, setK2Busy] = useState(false);
  const [k2Progress, setK2Progress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [k2Last, setK2Last] = useState(null);
  const nav = useNavigate();

  const loadK2Last = useCallback(() => {
    api.get("/recipes/import-k2/last").then((r) => setK2Last(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadK2Last(); }, [loadK2Last]);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/recipes")
      .then((r) => setRecipes(r.data))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Zadejte název receptury"); return; }
    const r = await api.post("/recipes", { ...form, author: form.author || "Neznámý" });
    toast.success("Receptura vytvořena");
    setNewOpen(false);
    setForm({ name: "", code: "", author: "", description: "" });
    nav(`/receptury/${r.data.id}`);
  };

  const duplicate = async (id) => {
    const r = await api.post(`/recipes/${id}/duplicate`);
    toast.success("Receptura zduplikována");
    load();
    nav(`/receptury/${r.data.id}`);
  };

  const del = async (id) => { await api.delete(`/recipes/${id}`); toast.success("Smazáno"); load(); };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? recipes.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.code || "").toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q) ||
        (r.author || "").toLowerCase().includes(q))
    : recipes;

  const doImport = async () => {
    if (!importForm.file) { toast.error("Vyberte soubor"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", importForm.file);
    try {
      const r = await api.post(`/recipes/import`, fd, {
        params: { name: importForm.name || "", author: importForm.author || "Neznámý" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receptura naimportována");
      setImportOpen(false);
      setImportForm({ name: "", author: "", file: null });
      nav(`/receptury/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import se nezdařil");
    } finally { setUploading(false); }
  };

  const doK2Import = async (full = false) => {
    const msg = full
      ? "Spustit ÚPLNÝ import všech receptur z K2? (ignoruje datum poslední změny)"
      : "Naimportovat nové a změněné receptury z K2? Chybějící suroviny se založí do katalogu.";
    if (!window.confirm(msg)) return;
    setK2Busy(true);
    setK2Progress(null);
    try {
      const r = await api.post(`/recipes/import-k2`, {}, { params: full ? { full: true } : {} });
      const jobId = r.data.job_id;
      if (r.data.already_running) toast.info("Import už běží, sleduji průběh…");
      else toast.info("Import z K2 spuštěn na pozadí…");
      pollK2(jobId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import z K2 se nezdařil");
      setK2Busy(false);
    }
  };

  const pollK2 = (jobId) => {
    const tick = async () => {
      try {
        const s = await api.get(`/recipes/import-k2/status/${jobId}`);
        const d = s.data;
        setK2Progress(d);
        if (d.status === "done") {
          toast.success(`Import z K2 hotov: ${d.created} nových, ${d.updated || 0} aktualizovaných receptur, ${d.materials_created} nových surovin (přeskočeno ${d.skipped}).`);
          setK2Busy(false);
          setK2Progress(null);
          load();
          loadK2Last();
          return;
        }
        if (d.status === "error") {
          toast.error(`Import z K2 selhal: ${d.error || "neznámá chyba"}`);
          setK2Busy(false);
          setK2Progress(null);
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        toast.error("Nepodařilo se načíst stav importu");
        setK2Busy(false);
        setK2Progress(null);
      }
    };
    setTimeout(tick, 1500);
  };

  return (
    <div className="fade-up">
      <PageHeader title="Receptury"
        subtitle={loading ? "Načítám receptury…" : `Předpisy výrobků, ze kterých tvoříte kalkulace · celkem ${recipes.length} receptur`}>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="recipe-template-btn"
          onClick={() => window.open(fileUrl(`/recipes/template`), "_blank")}>
          <Download className="h-4 w-4 mr-2" /> Šablona
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="recipe-import-btn"
          onClick={() => setImportOpen(true)}>
          <Upload className="h-4 w-4 mr-2" /> Import
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="recipe-import-k2-btn"
          onClick={() => doK2Import(false)} disabled={k2Busy}>
          {k2Busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {k2Busy
            ? (k2Progress?.total ? `Import z K2… ${Math.round((k2Progress.processed / k2Progress.total) * 100)}%` : "Import z K2…")
            : "Import z K2"}
        </Button>
        <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" data-testid="recipe-import-k2-full-btn"
          onClick={() => doK2Import(true)} disabled={k2Busy} title="Úplný import (ignoruje datum poslední změny)">
          <RotateCcw className="h-4 w-4" />
        </Button>
        <K2LastInfo last={k2Last} />
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-full" data-testid="recipe-new-btn"><Plus className="h-4 w-4 mr-2" /> Nová receptura</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Nová receptura</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <div><Label>Název *</Label><Input data-testid="recipe-form-name" className="mt-1.5" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. Svíčková na smetaně" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kód</Label><Input className="mt-1.5" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
                <div><Label>Autor</Label><Input className="mt-1.5" value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Zrušit</Button>
              <Button onClick={create} data-testid="recipe-form-submit">Vytvořit a upravit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="px-10 py-8">
        {k2Busy && k2Progress && (
          <div className="mb-6 bg-white border border-primary/25 rounded-xl px-5 py-4" data-testid="k2-progress-panel">
            <div className="flex justify-between items-center text-sm mb-2">
              <span className="font-medium flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                {k2Progress.phase === "stahování"
                  ? `Stahuji z K2… (${k2Progress.fetched || 0} záznamů)`
                  : k2Progress.phase === "zpracování"
                  ? `Zpracovávám ${k2Progress.processed || 0} z ${k2Progress.total || 0} záznamů`
                  : "Import z K2…"}
                {k2Progress.incremental && <span className="text-xs text-muted-foreground">· jen změny</span>}
              </span>
              <span className="num text-sm text-primary font-semibold">
                {k2Progress.total ? `${Math.round((k2Progress.processed / k2Progress.total) * 100)}%` : ""}
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300"
                data-testid="k2-progress-bar"
                style={{ width: k2Progress.total ? `${Math.min(100, (k2Progress.processed / k2Progress.total) * 100)}%` : (k2Progress.phase === "stahování" ? "15%" : "0%") }} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Nové: {k2Progress.created || 0} · Aktualizované: {k2Progress.updated || 0} · Nové suroviny: {k2Progress.materials_created || 0} · Přeskočeno: {k2Progress.skipped || 0}
            </div>
          </div>
        )}
        {!loading && recipes.length > 0 && (
          <div className="mb-6 flex items-center gap-3">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="recipe-search-input" value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Hledat podle názvu, kódu nebo autora…"
                className="pl-9 pr-9 rounded-full" />
              {query && (
                <button data-testid="recipe-search-clear" onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {q && (
              <span data-testid="recipe-search-count" className="text-sm text-muted-foreground">
                Nalezeno {filtered.length} z {recipes.length}
              </span>
            )}
          </div>
        )}
        {loading ? (
          <div data-testid="recipes-loading" className="bg-white border border-border rounded-xl px-6 py-16 text-center">
            <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
            <div className="mt-4 font-medium">Načítám receptury…</div>
            <div className="text-sm text-muted-foreground mt-1">Chvíli strpení, může jich být hodně.</div>
          </div>
        ) : recipes.length === 0 ? (
          <div className="bg-white border border-border rounded-xl px-6 py-16 text-center">
            <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div className="mt-4 font-medium">Zatím žádné receptury</div>
            <div className="text-sm text-muted-foreground mt-1">Stáhněte šablonu, vyplňte a naimportujte, nebo vytvořte novou.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-border rounded-xl px-6 py-16 text-center">
            <Search className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div className="mt-4 font-medium">Nic nenalezeno</div>
            <div className="text-sm text-muted-foreground mt-1">Zkuste jiný výraz. Hledáte „{query}".</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => (
              <div key={r.id} data-testid={`recipe-card-${r.id}`}
                className="group bg-white border border-border rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200 cursor-pointer"
                onClick={() => nav(`/receptury/${r.id}`)}>
                <div className="flex items-start justify-between">
                  <div className="pr-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-medium leading-tight">{r.name}</div>
                      {r.category && (
                        <span data-testid={`recipe-category-${r.id}`}
                          className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2 py-0.5 shrink-0"
                          title="Druh (kategorie z K2)">{r.category}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{r.code ? `${r.code} · ` : ""}{r.author}</div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => duplicate(r.id)} data-testid={`recipe-duplicate-${r.id}`}
                      title="Duplikovat" className="text-muted-foreground hover:text-primary p-1"><Copy className="h-4 w-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`recipe-delete-${r.id}`} title="Smazat" className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Smazat recepturu?</AlertDialogTitle>
                          <AlertDialogDescription>„{r.name}" bude odstraněna. Vytvořené kalkulace zůstanou.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Zrušit</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(r.id)}>Smazat</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-border flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {r.sections?.length || 0} sekcí</span>
                    <span>{r.items?.length || 0} surovin</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">
                  {r.calc_count || 0} kalkulací · poslední změna {fmtDate(r.last_changed || r.updated_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Import receptury z Excelu</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-muted-foreground">Stáhněte šablonu (Sekce, Kód suroviny, Název, MJ, Množství), vyplňte a nahrajte.</p>
            <div><Label>Název receptury</Label><Input data-testid="recipe-import-name" className="mt-1.5" value={importForm.name}
              onChange={(e) => setImportForm({ ...importForm, name: e.target.value })} placeholder="ponechte prázdné = z názvu souboru" /></div>
            <div><Label>Autor</Label><Input className="mt-1.5" value={importForm.author}
              onChange={(e) => setImportForm({ ...importForm, author: e.target.value })} /></div>
            <div><Label>Soubor (.xlsx)</Label>
              <Input type="file" accept=".xlsx,.xls" data-testid="recipe-import-file" className="mt-1.5"
                onChange={(e) => setImportForm({ ...importForm, file: e.target.files?.[0] || null })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Zrušit</Button>
            <Button onClick={doImport} disabled={uploading} data-testid="recipe-import-submit">
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Importovat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
